import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import './LegalPage.css';

export default function PrivacyPolicy() {
  return (
    <main className="legal-page" role="main">
      <Helmet>
        <title>Privacy Policy | EffyVenture — Effy Tech Venture Private Limited</title>
        <meta name="description" content="Privacy Policy of Effy Tech Venture Private Limited. Learn how we collect, use, protect, and retain your personal and business data in compliance with India's DPDPA 2023." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.effyventure.com/privacy-policy" />
      </Helmet>

      <div className="legal-page__hero">
        <div className="legal-page__hero-inner">
          <div className="legal-page__badge">Legal</div>
          <h1 className="legal-page__title">Privacy Policy</h1>
          <p className="legal-page__meta">
            <strong>Effy Tech Venture Private Limited</strong>
            <span className="legal-page__meta-sep">|</span>
            Effective Date: 8 May 2026
            <span className="legal-page__meta-sep">|</span>
            Version 1.0
          </p>
        </div>
      </div>

      <div className="legal-page__body">
        <div className="legal-page__toc">
          <h2 className="legal-page__toc-title">Table of Contents</h2>
          <ol className="legal-page__toc-list">
            <li><a href="#summary">Summary</a></li>
            <li><a href="#introduction">Introduction</a></li>
            <li><a href="#definitions">Definitions</a></li>
            <li><a href="#information-we-collect">Information We Collect</a></li>
            <li><a href="#how-we-collect">How We Collect Information</a></li>
            <li><a href="#legal-basis">Legal Basis for Processing</a></li>
            <li><a href="#how-we-use">How We Use Your Information</a></li>
            <li><a href="#sharing">Sharing of Information</a></li>
            <li><a href="#storage-retention">Data Storage, Retention &amp; Localisation</a></li>
            <li><a href="#security">Data Security</a></li>
            <li><a href="#your-rights">Your Rights Under Indian Law</a></li>
            <li><a href="#cookies">Cookies &amp; Tracking Technologies</a></li>
            <li><a href="#third-party">Third-Party Links &amp; Integrations</a></li>
            <li><a href="#childrens-privacy">Children's Privacy</a></li>
            <li><a href="#changes">Changes to This Privacy Policy</a></li>
            <li><a href="#contact">Grievance Officer &amp; Contact Details</a></li>
            <li><a href="#governing-law">Governing Law &amp; Jurisdiction</a></li>
          </ol>
        </div>

        <div className="legal-page__content">

          {/* Summary */}
          <section id="summary" className="legal-page__section">
            <div className="legal-page__summary-box">
              <h2 className="legal-page__summary-title">Summary — Your Privacy at a Glance</h2>
              <ul className="legal-page__summary-list">
                <li>We collect business and personal data you provide while using our platform.</li>
                <li>Your data is used solely to deliver, improve, and support our services.</li>
                <li>We do <strong>NOT</strong> sell your data to third parties.</li>
                <li>All data is stored on servers located within India.</li>
                <li>You may access, correct, or request deletion of your data at any time.</li>
                <li>We comply with India's Digital Personal Data Protection Act, 2023 (DPDPA) and the IT (SPDI) Rules, 2011.</li>
              </ul>
              <p className="legal-page__summary-note">
                This is a summary only. Please read the full policy below for complete details.
              </p>
            </div>
          </section>

          {/* 1. Introduction */}
          <section id="introduction" className="legal-page__section">
            <h2 className="legal-page__section-title">1. Introduction</h2>
            <p>
              Effy Tech Venture Private Limited ("Effy Tech Venture", "Company", "we", "us", or "our") is a company
              incorporated under the Companies Act, 2013, having its registered office in Chennai, Tamil Nadu, India.
              We provide a Software-as-a-Service (SaaS) platform that enables business owners and their authorized
              personnel to manage bookkeeping, billing, invoicing, GST compliance reporting, inventory, and financial
              analytics (collectively, the "Service"). The Service may be delivered via our cloud infrastructure,
              deployed on a local server at our premises, or installed on servers at the User's own premises,
              depending on the deployment model agreed upon.
            </p>
            <p>
              Regardless of the deployment model — cloud-hosted, on-premises at the User's site, or on servers at
              our own premises — this Privacy Policy applies in full to all Personal Data and Business Data processed
              through the Service.
            </p>
            <p>
              We are committed to protecting the privacy and security of your personal and business data. This
              Privacy Policy describes what information we collect, the purposes for which it is used, how it is
              protected, and the rights available to you under applicable Indian law. By accessing or using the
              Service, you confirm that you have read, understood, and agreed to this Privacy Policy.
            </p>
          </section>

          {/* 2. Definitions */}
          <section id="definitions" className="legal-page__section">
            <h2 className="legal-page__section-title">2. Definitions</h2>
            <dl className="legal-page__dl">
              <dt>"Personal Data"</dt>
              <dd>Any information that identifies or can identify a natural person, as defined under the Digital Personal Data Protection Act, 2023 (DPDPA).</dd>

              <dt>"Sensitive Personal Data or Information (SPDI)"</dt>
              <dd>Financial information, passwords, and other categories specified under the IT (SPDI) Rules, 2011.</dd>

              <dt>"Business Data"</dt>
              <dd>Financial records, invoices, ledger entries, inventory details, GST-related data, and other commercial information that your business generates or uploads to the Service.</dd>

              <dt>"Data Fiduciary"</dt>
              <dd>Refers to Effy Tech Venture Private Limited, which determines the purpose and means of processing Personal Data.</dd>

              <dt>"Data Principal"</dt>
              <dd>Refers to the individual (User or their authorized representative) whose Personal Data is processed.</dd>

              <dt>"User"</dt>
              <dd>Any individual or entity that registers for and uses the Service, including business owners, accountants, and authorized employees.</dd>
            </dl>
          </section>

          {/* 3. Information We Collect */}
          <section id="information-we-collect" className="legal-page__section">
            <h2 className="legal-page__section-title">3. Information We Collect</h2>

            <h3 className="legal-page__subsection-title">3.1 Account &amp; Identity Information</h3>
            <ul className="legal-page__list">
              <li>Full name, business name, and designation</li>
              <li>Email address and mobile number</li>
              <li>Username and encrypted password</li>
              <li>GST Identification Number (GSTIN), PAN, CIN, or other tax/regulatory identifiers</li>
              <li>Business address and registered office details</li>
            </ul>

            <h3 className="legal-page__subsection-title">3.2 Financial &amp; Business Data</h3>
            <ul className="legal-page__list">
              <li>Invoices, bills, purchase orders, credit notes, and receipts</li>
              <li>Customer and vendor master data including names, addresses, and GSTIN</li>
              <li>Transaction records, journal entries, and ledger data</li>
              <li>Bank account details provided for reconciliation purposes only</li>
              <li>Inventory records, product/SKU data, stock levels, and valuations</li>
              <li>Profit &amp; Loss statements, Balance Sheets, and other financial reports</li>
              <li>GST return data (GSTR-1, GSTR-3B, GSTR-9, and others as applicable)</li>
              <li>E-invoice and e-way bill data submitted via the platform</li>
            </ul>

            <h3 className="legal-page__subsection-title">3.3 Technical &amp; Usage Data</h3>
            <ul className="legal-page__list">
              <li>IP address, device type, operating system, and browser type</li>
              <li>Log data including pages visited, features used, and timestamps</li>
              <li>Session cookies and authentication tokens</li>
              <li>Crash reports and diagnostic error logs</li>
            </ul>

            <h3 className="legal-page__subsection-title">3.4 Communication Data</h3>
            <ul className="legal-page__list">
              <li>Support tickets, emails, and live-chat messages sent to us</li>
              <li>Feedback, survey responses, and product reviews</li>
            </ul>

            <h3 className="legal-page__subsection-title">3.5 Data We Do Not Collect</h3>
            <p>
              Effy Tech Venture does not intentionally collect biometric data, precise geo-location, or sensitive
              health information. We do not knowingly collect Personal Data from individuals under 18 years of age.
            </p>
          </section>

          {/* 4. How We Collect */}
          <section id="how-we-collect" className="legal-page__section">
            <h2 className="legal-page__section-title">4. How We Collect Information</h2>
            <ul className="legal-page__list">
              <li>Directly from you during registration, onboarding, and day-to-day use of the Service</li>
              <li>Through automated means such as cookies, server log files, and analytics tools</li>
              <li>Via integrations with third-party services you authorize (e.g., GSTN portal, NIC e-invoice system, banking APIs)</li>
              <li>From your team members or employees who access the Service under your account</li>
            </ul>
          </section>

          {/* 5. Legal Basis */}
          <section id="legal-basis" className="legal-page__section">
            <h2 className="legal-page__section-title">5. Legal Basis for Processing</h2>
            <p>We process your data on the following legal grounds under the DPDPA and applicable Indian law:</p>
            <ul className="legal-page__list">
              <li><strong>Consent:</strong> You have given explicit, informed consent during registration and through continued use of the Service.</li>
              <li><strong>Contractual necessity:</strong> Processing is required to perform the services under our Terms of Service agreement with you.</li>
              <li><strong>Legitimate interests:</strong> To maintain platform security, prevent fraud, improve features, and send relevant service communications.</li>
              <li><strong>Legal obligation:</strong> To comply with applicable Indian statutes including the Central Goods and Services Tax Act, 2017; Income Tax Act, 1961; Companies Act, 2013; and the Digital Personal Data Protection Act, 2023.</li>
            </ul>
          </section>

          {/* 6. How We Use */}
          <section id="how-we-use" className="legal-page__section">
            <h2 className="legal-page__section-title">6. How We Use Your Information</h2>

            <h3 className="legal-page__subsection-title">6.1 Service Delivery</h3>
            <ul className="legal-page__list">
              <li>Operating and maintaining the accounting, billing, invoicing, and GST compliance platform</li>
              <li>Processing transactions and generating financial documents and reports</li>
              <li>Enabling GST e-filing, e-invoicing (IRN generation), e-way bill creation, and reconciliation features</li>
              <li>Powering inventory management and P&amp;L / balance sheet analytics</li>
            </ul>

            <h3 className="legal-page__subsection-title">6.2 Account Management &amp; Security</h3>
            <ul className="legal-page__list">
              <li>Creating and managing your user account</li>
              <li>User authentication, session management, and access control</li>
              <li>Detecting and preventing unauthorized access, fraud, or misuse</li>
              <li>Sending security alerts, OTPs, and password-reset communications</li>
            </ul>

            <h3 className="legal-page__subsection-title">6.3 Service Improvement</h3>
            <ul className="legal-page__list">
              <li>Analysing usage patterns to improve features and user experience</li>
              <li>Debugging technical issues and fixing platform defects</li>
              <li>Conducting internal research, analytics, and product development</li>
            </ul>

            <h3 className="legal-page__subsection-title">6.4 Communication</h3>
            <ul className="legal-page__list">
              <li>Sending product updates, feature announcements, and release notes</li>
              <li>Responding to support requests, queries, and grievances</li>
              <li>Sending statutory notices or legally required communications</li>
            </ul>

            <h3 className="legal-page__subsection-title">6.5 Legal &amp; Regulatory Compliance</h3>
            <ul className="legal-page__list">
              <li>Complying with GST, income tax, and other applicable statutory requirements</li>
              <li>Responding to court orders, government directives, or legal proceedings</li>
              <li>Enforcing our Terms of Service and other agreements with Users</li>
            </ul>
          </section>

          {/* 7. Sharing */}
          <section id="sharing" className="legal-page__section">
            <h2 className="legal-page__section-title">7. Sharing of Information</h2>
            <p>
              Effy Tech Venture does not sell, rent, or trade your Personal Data or Business Data to any third
              party. We may share data only in the following limited circumstances:
            </p>

            <h3 className="legal-page__subsection-title">7.1 Service Providers &amp; Sub-processors</h3>
            <p>
              We engage trusted third-party vendors who process data on our behalf under strict contractual
              obligations. These include cloud hosting providers, payment processors, email and SMS delivery
              services, and customer support tools. All sub-processors are bound by data processing agreements
              consistent with this Privacy Policy.
            </p>
            <p>
              Our platform uses the following third-party integrations that may process your data as part of
              service delivery:
            </p>
            <ul className="legal-page__list">
              <li>
                <strong>Meta (Facebook/WhatsApp Business API):</strong> We use Meta's WhatsApp Business API
                (via the Facebook Graph API) to send order notifications and invoices to your customers'
                WhatsApp numbers, where you have enabled this feature. Data transmitted includes customer
                phone numbers, order summaries, and invoice details. This data is processed in accordance
                with <a href="https://www.facebook.com/privacy/policy/" target="_blank" rel="noopener noreferrer">Meta's Privacy Policy</a>.
                We do not share your personal account data with Meta beyond what is necessary for message
                delivery. You may disable WhatsApp notifications at any time from your account settings.
              </li>
              <li>
                <strong>Brevo (email delivery):</strong> Used to send transactional emails such as order
                confirmations, OTPs, and invoices. Data transmitted includes recipient email addresses and
                message content.
              </li>
              <li>
                <strong>Cloudinary:</strong> Used to store and serve inventory item images. Image files
                uploaded by you are stored on Cloudinary's servers.
              </li>
            </ul>

            <h3 className="legal-page__subsection-title">7.2 GST &amp; Government Portals</h3>
            <p>
              Where you authorize us, we transmit relevant GST and invoice data to the GSTN portal, the National
              Informatics Centre (NIC) for e-invoicing, or state tax authorities solely for the purpose of
              statutory compliance on your behalf.
            </p>

            <h3 className="legal-page__subsection-title">7.3 Legal Requirements</h3>
            <p>
              We may disclose data if required by law, court order, or a competent government or regulatory
              authority under applicable Indian law, including orders from the Income Tax Department, GST
              authorities, or CERT-In.
            </p>

            <h3 className="legal-page__subsection-title">7.4 Business Transfers</h3>
            <p>
              In the event of a merger, acquisition, restructuring, or sale of business assets, your data may
              be transferred to the successor entity, with prior notice to you and subject to equivalent privacy
              protections.
            </p>

            <h3 className="legal-page__subsection-title">7.5 With Your Consent</h3>
            <p>
              We may share data with third parties in any other circumstance where you have given prior explicit
              consent.
            </p>
          </section>

          {/* 8. Storage & Retention */}
          <section id="storage-retention" className="legal-page__section">
            <h2 className="legal-page__section-title">8. Data Storage, Retention &amp; Localisation</h2>
            <p>
              Effy Tech Venture operates the Service under three deployment models, and data storage obligations
              apply to each as follows:
            </p>
            <ul className="legal-page__list">
              <li>
                <strong>Cloud Deployment:</strong> Data is stored on servers located within India managed by
                Effy Tech Venture or its authorised cloud infrastructure partners. We do not transfer Personal
                Data outside the territory of India without your explicit consent and appropriate contractual
                safeguards as required under the DPDPA.
              </li>
              <li>
                <strong>On-Premises at User's Site:</strong> The software and all associated data reside on
                servers owned and operated by the User at their own premises. In this model, the User is
                primarily responsible for the physical security and operational maintenance of the servers.
                Effy Tech Venture may access data remotely only for support, maintenance, or troubleshooting
                purposes with your prior authorisation.
              </li>
              <li>
                <strong>On-Premises at Effy Tech Venture's Premises:</strong> The software and data are hosted
                on servers located at Effy Tech Venture's own facility in Chennai. Access is strictly controlled,
                and data remains within India at all times.
              </li>
            </ul>
            <p>
              Regardless of the deployment model, all data processed through the Service remains subject to
              this Privacy Policy, and Effy Tech Venture applies equivalent security standards across all
              environments.
            </p>
            <p>
              We retain your data for as long as your account is active or as necessary to provide the Service,
              comply with legal obligations, resolve disputes, and enforce our agreements. Upon account
              termination, we will delete or anonymise your data within <strong>90 days</strong>, except where
              a longer retention period is mandated by law. For example, GST-related records are retained for
              a minimum of <strong>6 years</strong> as required under the Central Goods and Services Tax Act,
              2017.
            </p>
          </section>

          {/* 9. Security */}
          <section id="security" className="legal-page__section">
            <h2 className="legal-page__section-title">9. Data Security</h2>
            <p>
              Effy Tech Venture implements industry-standard technical and organisational security measures to
              protect your data:
            </p>
            <ul className="legal-page__list">
              <li>AES-256 encryption for data at rest</li>
              <li>TLS 1.2 or higher encryption for all data in transit</li>
              <li>Multi-factor authentication (MFA) options for User accounts</li>
              <li>Role-based access controls (RBAC) within the platform</li>
              <li>Regular security audits, vulnerability assessments, and penetration testing</li>
              <li>Strict access controls limiting employee access to data on a need-to-know basis</li>
              <li>Incident response procedures aligned with CERT-In Cyber Security Incident Reporting guidelines</li>
            </ul>
            <p>
              While we take all commercially reasonable precautions, no information system is entirely immune
              to security risks. In the event of a personal data breach, we will notify affected Users and,
              once operational, the Data Protection Board of India within the timelines prescribed under the
              DPDPA.
            </p>
          </section>

          {/* 10. Your Rights */}
          <section id="your-rights" className="legal-page__section">
            <h2 className="legal-page__section-title">10. Your Rights Under Indian Law</h2>
            <p>
              Under the Digital Personal Data Protection Act, 2023 and applicable IT Rules, you have the
              following rights as a Data Principal:
            </p>

            <h3 className="legal-page__subsection-title">10.1 Right to Access Information</h3>
            <p>You may request a summary of the Personal Data we hold about you and how it is being processed.</p>

            <h3 className="legal-page__subsection-title">10.2 Right to Correction &amp; Completeness</h3>
            <p>You may request correction, completion, or updating of inaccurate or incomplete Personal Data.</p>

            <h3 className="legal-page__subsection-title">10.3 Right to Erasure</h3>
            <p>
              You may request deletion of your Personal Data where it is no longer necessary for the purposes
              for which it was collected, subject to our statutory retention obligations under the CGST Act,
              Income Tax Act, and other applicable laws.
            </p>

            <h3 className="legal-page__subsection-title">10.4 Right to Grievance Redressal</h3>
            <p>
              You may contact our Grievance Officer (see Section 15) to lodge a complaint. We will acknowledge
              your complaint promptly and endeavour to resolve it within 30 days.
            </p>

            <h3 className="legal-page__subsection-title">10.5 Right to Nominate</h3>
            <p>
              Under the DPDPA, you may nominate an individual to exercise your rights on your behalf in the
              event of your death or incapacity.
            </p>

            <h3 className="legal-page__subsection-title">10.6 Right to Withdraw Consent</h3>
            <p>
              You may withdraw consent to processing at any time by contacting us. Withdrawal of consent will
              not affect the lawfulness of processing carried out prior to withdrawal. Please note that
              withdrawal may affect your ability to use certain features of the Service.
            </p>

            <p>
              To exercise any of the above rights, please write to us using the contact details in Section 15.
              We will respond within <strong>30 days</strong> of receiving a verifiable request.
            </p>
          </section>

          {/* 11. Cookies */}
          <section id="cookies" className="legal-page__section">
            <h2 className="legal-page__section-title">11. Cookies &amp; Tracking Technologies</h2>
            <p>We use the following types of cookies and similar technologies on our platform:</p>
            <ul className="legal-page__list">
              <li><strong>Essential cookies:</strong> Required for the platform to operate correctly, including session authentication and security tokens.</li>
              <li><strong>Analytics cookies:</strong> Help us understand how Users interact with the platform so we can improve it. We use internal analytics tools and do not share this data with advertising networks.</li>
              <li><strong>Preference cookies:</strong> Remember your settings, language preferences, and dashboard configurations.</li>
            </ul>
            <p>
              You may manage or disable cookies through your browser settings. Disabling essential cookies will
              impair platform functionality. We do not use cookies for advertising, re-targeting, or cross-site
              behavioural profiling.
            </p>
          </section>

          {/* 12. Third-Party Links */}
          <section id="third-party" className="legal-page__section">
            <h2 className="legal-page__section-title">12. Third-Party Links &amp; Integrations</h2>
            <p>
              The Service may contain links to third-party websites or integrate with external services such as
              the GSTN portal, NIC e-invoice system, banking APIs, payment gateways, and the Meta WhatsApp
              Business API. This Privacy Policy applies solely to data processed by Effy Tech Venture and does
              not extend to those third-party services. We encourage you to review the privacy policies of any
              third-party service before sharing your data with them.
            </p>
            <p>
              Our use of the Meta WhatsApp Business API is subject to Meta's
              {' '}<a href="https://www.facebook.com/privacy/policy/" target="_blank" rel="noopener noreferrer">Privacy Policy</a>{' '}
              and{' '}
              <a href="https://www.whatsapp.com/legal/terms-of-service" target="_blank" rel="noopener noreferrer">WhatsApp Terms of Service</a>.
              Messages sent via WhatsApp are for transactional notifications only (order updates, invoices,
              OTPs). We do not use this channel for unsolicited marketing.
            </p>
          </section>

          {/* 13. Children's Privacy */}
          <section id="childrens-privacy" className="legal-page__section">
            <h2 className="legal-page__section-title">13. Children's Privacy</h2>
            <p>
              The Service is intended exclusively for business use by adults aged 18 years and above. We do not
              knowingly collect Personal Data from minors. If we become aware that we have inadvertently
              collected data from a person under 18, we will delete it promptly.
            </p>
          </section>

          {/* 14. Changes */}
          <section id="changes" className="legal-page__section">
            <h2 className="legal-page__section-title">14. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy periodically to reflect changes in our practices, technology,
              or applicable law. We will notify you of material changes via email or a prominent in-platform
              notice at least <strong>15 days</strong> before the revised Policy takes effect. Your continued
              use of the Service after the effective date of any revision constitutes acceptance of the updated
              Policy.
            </p>
            <p>A version history of this Privacy Policy is maintained and is available to Users on request.</p>
          </section>

          {/* 15. Contact */}
          <section id="contact" className="legal-page__section">
            <h2 className="legal-page__section-title">15. Grievance Officer &amp; Contact Details</h2>
            <p>
              In accordance with the Information Technology Act, 2000, the IT (Intermediary Guidelines and
              Digital Media Ethics Code) Rules, 2021, and the Digital Personal Data Protection Act, 2023, the
              details of our designated Grievance Officer are as follows:
            </p>
            <div className="legal-page__contact-card">
              <h3 className="legal-page__contact-title">Grievance Officer — Effy Tech Venture Private Limited</h3>
              <table className="legal-page__contact-table">
                <tbody>
                  <tr><th>Name</th><td>Vimal Raj</td></tr>
                  <tr><th>Company</th><td>Effy Tech Venture Private Limited</td></tr>
                  <tr><th>Address</th><td>E4 36, 4 46, D S Nagar Mannivakkam, Vandalur, Tambaram, Kanchipuram, Chennai, Tamil Nadu — 600048, India</td></tr>
                  <tr><th>Email</th><td><a href="mailto:info@effyventure.com">info@effyventure.com</a></td></tr>
                  <tr><th>Phone</th><td><a href="tel:+919043395293">+91-9043395293</a></td></tr>
                  <tr><th>Working Hours</th><td>Monday to Friday, 10:00 AM – 6:00 PM IST (excluding public holidays)</td></tr>
                  <tr><th>General Support / Legal Notices</th><td><a href="mailto:info@effyventure.com">info@effyventure.com</a></td></tr>
                </tbody>
              </table>
            </div>
            <p>
              We are committed to resolving all complaints within <strong>30 days</strong> of receipt. If you
              are dissatisfied with our response, you may escalate your grievance to the Data Protection Board
              of India (once constituted under the DPDPA) or seek relief from a court of competent jurisdiction.
            </p>
          </section>

          {/* 16. Governing Law */}
          <section id="governing-law" className="legal-page__section">
            <h2 className="legal-page__section-title">16. Governing Law &amp; Jurisdiction</h2>
            <p>
              This Privacy Policy shall be governed by and construed in accordance with the laws of the Republic
              of India. Any dispute, claim, or controversy arising out of or relating to this Privacy Policy
              shall be subject to the exclusive jurisdiction of the courts and tribunals located in Chennai,
              Tamil Nadu, India.
            </p>
          </section>

          <div className="legal-page__footer-note">
            <p>— End of Privacy Policy —</p>
            <p>© Effy Tech Venture Private Limited. All rights reserved.</p>
            <p>
              <Link to="/terms-of-service">Terms of Service</Link>
              {' '} | {' '}
              <Link to="/contact">Contact Us</Link>
            </p>
          </div>

        </div>
      </div>
    </main>
  );
}
